import React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DeliveryItem as DeliveryItemData } from "./utils/deliveryChecklist";

interface DeliveryItemProps {
  item: DeliveryItemData;
  /** Asks TACO to produce this deliverable. */
  onAsk: (item: DeliveryItemData) => void;
}

/**
 * One row of the delivery checklist: a checkbox, the label, and the purpose text
 * the agent wrote to set its quality bar.
 *
 * An empty box is an invitation, not a failure — so every open item offers "Ask
 * TACO for this". That is also what makes the checked-state inference safe: an
 * item wrongly shown as open costs one redundant request, nothing more.
 */
const DeliveryItem: React.FC<DeliveryItemProps> = ({ item, onAsk }) => (
  <div className="group px-4 py-2.5 border-b border-border last:border-b-0">
    <div className="flex items-center gap-2">
      <span
        className={cn(
          "grid h-[15px] w-[15px] flex-none place-items-center rounded border-[1.5px]",
          item.isDone
            ? "border-green-700 bg-green-700 text-white dark:border-green-500 dark:bg-green-500"
            : "border-border text-transparent",
        )}
      >
        <Check className="h-2.5 w-2.5" />
      </span>

      <span
        className={cn(
          "text-[12.5px] font-semibold",
          item.isDone ? "text-muted-foreground" : "text-foreground",
        )}
      >
        {item.label}
      </span>

      {!item.isDone && (
        <button
          type="button"
          onClick={() => onAsk(item)}
          className="ml-auto flex-none rounded-full border border-border px-2 py-px text-[10.5px] font-semibold text-blue-600 opacity-0 transition-opacity hover:border-blue-600 hover:bg-accent group-hover:opacity-100 dark:text-blue-400 focus-visible:opacity-100"
        >
          Ask TACO for this
        </button>
      )}
    </div>

    {item.purpose && (
      <p className="pl-[25px] pt-0.5 text-[11.5px] leading-relaxed text-muted-foreground">
        {item.purpose}
      </p>
    )}
  </div>
);

export default DeliveryItem;
